import { CONNECTOR_END_STYLE } from '../../constants';
import Connector, { type Attachable } from '../../objects/Connector';
import type EmbeddedTextBehaviors from '../../objects/EmbeddedTextBehaviorsV2';
import { AttachmentPoint, ConnectorEnd } from '../../types';
import {
  allowsCenterAttachment,
  calculateAttachmentPosition,
} from '../../utils/connectorAttachment';
import {
  hasEmbeddedTextBehaviors,
  hasConnectorAttachmentBehaviours,
} from '../../objects/guards';
import { type PastedWhiteboardContent } from '../../collaboration/Serialization';

/**
 * Given a shape, create a duplicate of it to the right. If the original shape
 * had an incoming connector, connect the new shape with the old one via a
 * connector.
 */
export function createSequentialShape(
  fabricObject: fabric.Object & EmbeddedTextBehaviors
) {
  fabricObject.endTextEditing();

  this.canvas.setActiveObject(fabricObject);

  const originalShape = fabricObject;
  const hasConnectors =
    hasConnectorAttachmentBehaviours(originalShape) &&
    (originalShape as Attachable).connectorAttachments &&
    (originalShape as Attachable).connectorAttachments.length > 0;

  const moveCallback = (duplicatedObjects: fabric.Object[]) => {
    if (!duplicatedObjects || duplicatedObjects.length === 0) return;

    const newShape = duplicatedObjects[0];

    if (!hasEmbeddedTextBehaviors(newShape)) {
      throw new Error(
        'Expected duplicated shape to have embedded text behaviors'
      );
    }

    if (
      hasConnectors &&
      hasConnectorAttachmentBehaviours(newShape) &&
      allowsCenterAttachment(newShape as Attachable)
    ) {
      const sourceAttachment = AttachmentPoint.Center;
      const targetAttachment = AttachmentPoint.Center;

      const startPoint = calculateAttachmentPosition(
        originalShape as Attachable,
        sourceAttachment,
        { withStandoff: true }
      );
      const endPoint = calculateAttachmentPosition(
        newShape as Attachable,
        targetAttachment,
        { withStandoff: true }
      );

      if (startPoint && endPoint) {
        const connector = new Connector([startPoint, endPoint], {
          controller: this,
          ...this.defaultsManager.for('connector'),
          endStyles: [
            CONNECTOR_END_STYLE.NONE,
            CONNECTOR_END_STYLE.FILLED_ARROW,
          ],
        });

        this.addToCanvas(connector);

        (originalShape as Attachable).attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_START,
          sourceAttachment
        );
        (newShape as Attachable).attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_END,
          targetAttachment
        );

        connector.constrainConnector({});
      }
    }

    this.canvas.setActiveObject(newShape);

    // Mark the new shape as newly created to enable continued Tab chaining
    newShape.isNewlyCreated = true;

    setTimeout(() => {
      void newShape.startTextEditing(true);
    }, 100);
  };

  const mutateBeforeSerializeCallback = (
    content: PastedWhiteboardContent<any>
  ) => {
    const firstObject = content.objects && Object.values(content.objects)[0];

    if (firstObject) {
      // Clear text content for the duplicated shape
      firstObject.textContent = '';

      const spacing = 150;
      firstObject.left += spacing;
    }
  };

  this.canvas.duplicateActiveSelection(
    moveCallback,
    mutateBeforeSerializeCallback
  );

  this.actionFocus(false);
}
